import {useSelector} from "react-redux";
import {RootState} from "../../store";
import {UserInfo} from "../i-auth";

export type CustomerIdentity = {
  emailAddress: string;
  firstName: string;
  lastName: string;
};

/**
 * The logged-in customer's own identity, read out of the persisted session.
 *
 * The session does not store this in one place. Three login paths write three
 * shapes, and all of them are live:
 *
 * - customer activation / verify-link (`activateCustomerUser.fulfilled`) writes
 *   `{user: {firstName, lastName, emailAddress}}` — note `emailAddress`, and
 *   note that it writes to `user`, not `customer`;
 * - `normalizeV2CustomerAuthResponse` writes `{customer: {email, emailAddress}}`;
 * - a coach who also holds a customer account keeps `{user: {email}}`.
 *
 * Reading only `customer.email ?? user.email` therefore yields `""` for anyone
 * who arrived through the activation link — the most common customer login —
 * and an empty email is silently sent to the API, which rejects it
 * ("emailAddress should not be empty"). This is the one tolerant reader for
 * that fact; prefer it over reaching into `userInfo` at a call site.
 */
export const selectCustomerIdentity = (
  userInfo: UserInfo | null | undefined
): CustomerIdentity => {
  const customer = userInfo?.customer;
  const user = userInfo?.user;

  return {
    emailAddress: (
      customer?.emailAddress ??
      customer?.email ??
      user?.emailAddress ??
      user?.email ??
      ""
    ).trim(),
    firstName: customer?.firstName ?? user?.firstName ?? "",
    lastName: customer?.lastName ?? user?.lastName ?? "",
  };
};

export const useCustomerIdentity = (): CustomerIdentity => {
  const {userInfo} = useSelector((state: RootState) => state.userLogin);
  return selectCustomerIdentity(userInfo);
};
